/**
 * /accept-invitation — token-hash invitation redemption.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §8.2`:
 *   1. Read `?token=...&email=...` from URL.
 *   2. POST `/accept-invitation` with `{ token, password, full_name? }`.
 *      Server response is `{ profile_id, auth_user_id }` ONLY — no JWT,
 *      no session.
 *   3. Run `supabase.auth.signInWithPassword({ email, password })` to
 *      establish the session.
 *   4. Bounce to `/app`.
 *
 * Recovery branch: if step 3 fails (URL email tampered or stale), prompt
 * the user to sign in via `/login` directly. The token is already
 * consumed; the auth.users row exists; their password is what they just
 * set. A normal `/login` sign-in works.
 *
 * Visual upgrade (Task 040): same split-screen `<AuthShell>` as
 * `/login`. All five stages (form / submitting / signing-in / recovery
 * / invalid + transient success) are preserved verbatim.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../auth/supabase';
import { acceptInvitation } from '../api/endpoints';
import { isApiError } from '../api/client';
import { env } from '../env';
import { AuthShell } from '../components/auth/AuthShell';

type Stage = 'form' | 'submitting' | 'signing-in' | 'recovery' | 'invalid' | 'success';

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const emailFromUrl = params.get('email') ?? '';
  const navigate = useNavigate();

  const [email] = useState(emailFromUrl);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [stage, setStage] = useState<Stage>(token.length === 0 ? 'invalid' : 'form');
  const [error, setError] = useState<string | null>(null);

  if (stage === 'invalid' || token.length === 0) {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <div className="auth-state-glyph auth-state-glyph--error">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          </div>
          <div className="auth-eyebrow" style={{ textAlign: 'center' }}>
            دعوة غير صالحة
          </div>
          <h1 className="auth-title" style={{ textAlign: 'center' }}>
            رابط الدعوة <em>غير صالح</em>
          </h1>
          <p className="auth-sub" style={{ textAlign: 'center', marginBottom: 24 }}>
            الرابط ناقص أو منتهي الصلاحية. اطلب من Owner الوكالة إرسال دعوة جديدة.
          </p>
          <Link to="/login" className="auth-cta" style={{ textDecoration: 'none' }}>
            <span>العودة إلى تسجيل الدخول</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </div>
      </AuthShell>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('كلمة المرور يجب أن تكون 12 خانة على الأقل.');
      return;
    }
    if (!env.isConfigured) {
      setError('متغيرات البيئة Supabase غير مهيّأة. راجع apps/web/.env.example.');
      return;
    }
    setStage('submitting');
    try {
      await acceptInvitation({
        token,
        password,
        full_name: fullName.trim().length > 0 ? fullName.trim() : null,
      });
      setStage('signing-in');
      if (!email) {
        // No email in URL → cannot complete sign-in here. Send to /login.
        setStage('recovery');
        return;
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) {
        setStage('recovery');
        return;
      }
      setStage('success');
      navigate('/app', { replace: true });
    } catch (err) {
      if (isApiError(err)) {
        if (err.code === 'not_found') {
          setStage('invalid');
          return;
        }
        if (err.code === 'validation_error') {
          setError('البيانات غير صالحة. تأكد من كلمة المرور وحاول مجدداً.');
          setStage('form');
          return;
        }
        if (err.code === 'entitlement_denied') {
          // Task 300A. TWO causes, not one: the seats are full, or the
          // organization is not operational. `getFeatureLimit` returns
          // 0 standing-first, and unlike `create-client` this route
          // has no `requireOperationalOrganization` ahead of it (it is
          // also outside `CommercialStandingGate`), so standing really
          // does reach here — a seat denial does not mean the seats
          // are full. The lead names no cause, the Owner's lever is
          // conditional on the one it addresses, and the fallback asks
          // Super Agency to check.
          setError(
            'تعذّر إكمال قبول الدعوة حالياً. تواصل مع مالك الوكالة: إن كان السبب امتلاء المقاعد فبإمكانه إنهاء دور غير مستخدم لإتاحة مقعد؛ وإلا فيلزم التواصل مع فريق Super Agency للتحقق من حالة حساب الوكالة أو إعداد خطتها.',
          );
          setStage('form');
          return;
        }
      }
      setError('تعذّر إكمال قبول الدعوة. حاول مجدداً.');
      setStage('form');
    }
  }

  if (stage === 'recovery') {
    return (
      <AuthShell>
        <div className="auth-eyebrow">تم إنشاء الحساب</div>
        <h1 className="auth-title">
          الحساب <em>جاهز</em>
        </h1>
        <p className="auth-sub">
          لم نتمكن من إكمال تسجيل الدخول تلقائياً (قد يكون البريد في الرابط مختلفاً عن البريد
          المربوط بالدعوة). افتح صفحة تسجيل الدخول واستخدم بريدك الفعلي وكلمة المرور التي اخترتها
          للتو.
        </p>
        <Link to="/login" className="auth-cta" style={{ textDecoration: 'none' }}>
          <span>الانتقال إلى تسجيل الدخول</span>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
      </AuthShell>
    );
  }

  const ctaLabel =
    stage === 'submitting'
      ? 'جاري قبول الدعوة…'
      : stage === 'signing-in'
        ? 'جاري تسجيل الدخول…'
        : 'قبول الدعوة وإنشاء الحساب';

  return (
    <AuthShell>
      <span className="auth-chip">تمت دعوتك</span>
      <div className="auth-eyebrow">إكمال إنشاء الحساب</div>
      <h1 className="auth-title">
        أنشئ كلمة <em>المرور</em>
      </h1>
      <p className="auth-sub">
        أدخل كلمة مرور آمنة (12 خانة على الأقل) لإكمال إنشاء حسابك. يمكنك تعديل اسمك الكامل لاحقاً
        من الإعدادات.
      </p>

      <form onSubmit={onSubmit} noValidate>
        {email ? (
          <div className="banner banner-info" style={{ marginBottom: 16 }}>
            <strong>البريد:</strong>&nbsp;{email}
          </div>
        ) : (
          <div className="banner banner-info" style={{ marginBottom: 16 }}>
            البريد في الرابط مفقود. سيتم إنشاء الحساب، ثم تسجيل الدخول من
            <Link to="/login" style={{ marginInline: 4, fontWeight: 600 }}>
              صفحة الدخول
            </Link>
            بعد إكمال هذا النموذج.
          </div>
        )}
        {!env.isConfigured ? (
          <div className="banner banner-info" style={{ marginBottom: 16 }}>
            متغيرات Supabase غير مهيّأة محلياً. راجع apps/web/.env.example.
          </div>
        ) : null}
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="full_name">
            الاسم الكامل (اختياري)
          </label>
          <div className="auth-input-wrap">
            <input
              id="full_name"
              className="auth-input"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.currentTarget.value)}
              placeholder="مثال: أحمد القحطاني"
              disabled={stage !== 'form'}
              autoComplete="name"
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
          </div>
        </div>

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="password">
            كلمة المرور
          </label>
          <div className="auth-input-wrap">
            <input
              id="password"
              className="auth-input auth-input--has-eye"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              placeholder="12 خانة على الأقل"
              disabled={stage !== 'form'}
              autoComplete="new-password"
              dir="ltr"
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="4" y="10" width="16" height="11" rx="2" />
              <path d="M8 10V7a4 4 0 1 1 8 0v3" />
            </svg>
            <button
              type="button"
              className="auth-input-eye"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              aria-pressed={showPassword}
              disabled={stage !== 'form'}
            >
              {showPassword ? (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M3 3l18 18" />
                  <path d="M10.6 6.1A11.3 11.3 0 0 1 12 6c6 0 10 6 10 6a14.4 14.4 0 0 1-3 3.6" />
                  <path d="M6.7 6.7A14.5 14.5 0 0 0 2 12s4 6 10 6c1.6 0 3-.4 4.3-1" />
                  <circle cx="12" cy="12" r="2.5" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="auth-cta"
          disabled={stage !== 'form'}
          style={{ marginTop: 8 }}
        >
          {stage === 'submitting' || stage === 'signing-in' ? <span className="spinner" /> : null}
          <span>{ctaLabel}</span>
          {stage === 'form' ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          ) : null}
        </button>

        <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
          الرابط مرتبط ببريد الدعوة من الخادم — لا يمكن تغييره من هنا.
        </p>
      </form>
    </AuthShell>
  );
}
